// Composer Studio — 노드/위젯 복사·붙여넣기 공용 순수 함수
//
// 이 파일이 소유하는 것: 클립보드 유틸 + 의존 소스 수집, payload v2(위젯 동봉 + v1 하위호환 파서),
// 붙여넣기 본체(applyWidgetClipboardPaste — 클론·키 재발급·의존 병합), 이식 리포트 조립
// (buildWidgetPasteReport). 전부 **순수 계산**이다.
// 이 파일 밖: OS/앱 클립보드 채널 read/write 배선(EditorPage `writeClipboardPayloadChannels`),
// DOM 이벤트·React state·undo 스텝·모달 표시.
//
// 노드 트리를 복사할 때, 그 노드들이 참조하는 "템플릿 전역 데이터 소스"(dataDefs/dataSources)를
// 함께 수집해 붙여넣기 대상 템플릿에 이식할 수 있도록 payload를 구성한다.
//
// 노드가 참조하는 템플릿 스코프 데이터 소스는 `over` 문자열로만 참조된다:
//   - RepeatNode.over (kind:'repeat')
//   - TableNode.repeatRegions[].over (TableRepeatRegion)
//   - table 셀 집계 CellAggregate.over (TableCell.aggregate)
// `over` 값은 ReportTemplate.dataDefs[].key 또는 dataSources[].key 를 가리킨다.
// ⚠️ binding.catalogKey·DataSourceDef.primary·DataSourceLookup.source 는 전역 카탈로그(템플릿 스코프
//    아님) 참조라 의존 소스 수집 대상이 아니다 — `over`만 수집한다.

import type {
  ApprovalPlacement,
  CatalogIndex,
  ComposerNode,
  ComposerWidget,
  DataDef,
  DataSourceDef,
  InputWidget,
  ReportTemplate,
  SpecialNode,
} from '@safetyhub/shared';
import {
  REGISTERED_FORM_WIDGETS_META,
  SERVER_SYNTHESIZED_CATALOG_ROW_KEYS,
} from '@safetyhub/shared';
import {
  buildTableIndex,
  clonePresetSubtreeForInsert,
  collectPlacedInputKeys,
  createWriteFormBindingFilter,
  effectiveOptionsSources,
  isDerivedInput,
  parseBatchWriteTarget,
  walkBindings,
} from '@safetyhub/report-composer';
import type { PresetUnresolvedRef } from '@safetyhub/report-composer';
import { cloneNodesWithNewIds } from './nodeTreeClone';
import { dataDefEqual } from './dataDefFieldsNormalize';

/**
 * 노드 트리에 결재란(approvalBlock) SpecialNode가 하나라도 있는지 재귀 판정한다.
 *
 * 순회 경계는 collectOverKeys/cloneNodeWithNewIds와 동일:
 *   group/stack → children, table → cells[].content, repeat → template, branch → branches[].children.
 * 결재란은 `kind:'special'` + `special:'approvalBlock'` 마커 노드다(메타는 템플릿 레벨에 별도).
 * 복사한 노드에 결재란이 포함될 때만 템플릿 레벨 결재 메타(approvalLineMeta/approvalPlacement)를
 * payload에 함께 실어 붙여넣기 대상으로 이식할 근거로 쓴다.
 */
export function treeHasApprovalBlock(nodes: ComposerNode[]): boolean {
  const walk = (list: ComposerNode[]): boolean => {
    for (const n of list) {
      if (n.kind === 'special' && (n as SpecialNode).special === 'approvalBlock') {
        return true;
      }
      if (n.kind === 'group' || n.kind === 'stack') {
        if (walk(n.children)) return true;
      } else if (n.kind === 'table') {
        for (const cell of n.cells) {
          if (cell.content && walk(cell.content)) return true;
        }
      } else if (n.kind === 'repeat') {
        if (walk([n.template])) return true;
      } else if (n.kind === 'branch') {
        for (const slot of n.branches) {
          if (walk(slot.children)) return true;
        }
      }
    }
    return false;
  };
  return walk(nodes);
}

/**
 * 노드 트리를 재귀 순회하며 모든 템플릿 스코프 `over` 키를 수집한다(중복 제거, 삽입 순서 보존).
 *
 * 순회 기준은 `nodeTreeClone.ts`의 cloneNodeWithNewIds(reassign)와 동일:
 *   group/stack → children, table → cells[].content, repeat → template, branch → branches[].children.
 * 각 노드에서 수집하는 over:
 *   - repeat.over, table.repeatRegions[].over, table.cells[].aggregate.over.
 * 빈 문자열/undefined는 제외한다.
 */
export function collectOverKeys(nodes: ComposerNode[]): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();

  const add = (over: string | undefined) => {
    if (!over) return;
    if (seen.has(over)) return;
    seen.add(over);
    keys.push(over);
  };

  const walk = (list: ComposerNode[]) => {
    for (const n of list) {
      if (n.kind === 'group' || n.kind === 'stack') {
        walk(n.children);
      } else if (n.kind === 'table') {
        for (const region of n.repeatRegions ?? []) add(region.over);
        for (const cell of n.cells) {
          add(cell.aggregate?.over);
          if (cell.content) walk(cell.content);
        }
      } else if (n.kind === 'repeat') {
        add(n.over);
        walk([n.template]);
      } else if (n.kind === 'branch') {
        for (const slot of n.branches) walk(slot.children);
      }
    }
  };

  walk(nodes);
  return keys;
}

/**
 * 노드 트리가 참조하는 over 키를 template.dataDefs/dataSources에서 찾아 매칭되는 def만 반환한다.
 *
 * 각 over 키를 dataDefs(.key 일치)·dataSources(.key 일치)에서 조회한다. 실제 스키마상 한 key가 양쪽에
 * 동시 존재하지는 않으나, 만일 둘 다 매칭되면 **dataSources를 우선**(더 구체적인 조립 선언)하고 dataDef는
 * 담지 않는다. 어느 쪽에도 매칭 안 되는 over(전역 카탈로그 직접 참조 등)는 무시한다.
 * 반환 def는 중복 없이(같은 key 한 번만), 참조된 것만 포함한다(템플릿의 무관 def는 제외).
 */
export function collectNodeDependencies(
  nodes: ComposerNode[],
  template: Pick<ReportTemplate, 'dataDefs' | 'dataSources'>,
): { dataDefs: DataDef[]; dataSources: DataSourceDef[] } {
  const overKeys = collectOverKeys(nodes);
  const dataDefs: DataDef[] = [];
  const dataSources: DataSourceDef[] = [];

  for (const key of overKeys) {
    // dataSources 우선 매칭(둘 다 있으면 dataSource 채택, dataDef는 skip).
    const source = template.dataSources?.find((s) => s.key === key);
    if (source) {
      dataSources.push(source);
      continue;
    }
    const def = template.dataDefs?.find((d) => d.key === key);
    if (def) dataDefs.push(def);
  }

  return { dataDefs, dataSources };
}

/** 노드 클립보드 localStorage 키(실제 read/write 배선은 EditorPage `writeClipboardPayloadChannels` 몫). */
export const NODE_CLIPBOARD_KEY = 'composer:node-clipboard';

/**
 * OS 클립보드(navigator.clipboard 텍스트 채널)용 sentinel 접두사.
 *
 * localStorage는 origin(도메인) 단위로 격리돼 상용계↔개발계처럼 다른 origin 간에는 공유되지 않는다.
 * crossorigin(다른 환경)·원격 데스크톱 클립보드 동기화로 노드를 옮기려면 OS 클립보드(텍스트)에 실어야 한다.
 * 임의의 클립보드 텍스트(사용자가 복사한 일반 문자열)와 노드 payload를 구분하기 위해 이 접두사를 붙인다.
 * 형식: `<PREFIX><serializeNodeClipboard(payload) JSON>`.
 *
 * ⚠️ 접두사의 `v1`은 **채널 sentinel**이지 payload 버전이 아니다 — payload v2에서도 **그대로 유지**한다.
 * 접두사를 바꾸면 신·구 admin이 서로의 클립보드 텍스트를 아예 못 알아봐(startsWith 실패) 노드 복사까지
 * 끊긴다. 버전 협상은 payload 내부 `version` 필드가 담당한다.
 */
export const NODE_CLIPBOARD_TEXT_PREFIX = 'safetyhub-composer-nodes/v1\n';

/**
 * OS 클립보드 텍스트(접두사 포함)를 payload로 파싱한다. 접두사가 없으면(노드 payload 아님) null.
 * 접두사 이후 본문을 parseNodeClipboard로 검증한다.
 */
export function parseNodeClipboardText(text: string | null): NodeClipboardPayload | null {
  if (!text) return null;
  // ⚠️ Windows는 OS 클립보드 텍스트의 개행을 정규화한다(\n→\r\n). 접두사 끝의 \n이 \r\n으로 바뀌면
  // startsWith가 어긋나 sentinel을 못 알아본다 → raw \r를 제거한 뒤 매칭한다. 직렬화 JSON은 raw \r/\n을
  // 포함하지 않으므로(개행은 \\n으로 이스케이프) 전역 \r 제거는 본문을 손상시키지 않는다.
  const clean = text.replace(/\r/g, '');
  if (!clean.startsWith(NODE_CLIPBOARD_TEXT_PREFIX)) return null;
  return parseNodeClipboard(clean.slice(NODE_CLIPBOARD_TEXT_PREFIX.length));
}

/**
 * 노드 클립보드 payload — 복사한 노드/위젯 + 그들이 참조하는 템플릿 스코프 데이터 소스 + 출처 템플릿 키.
 * 붙여넣기 시(P3) 대상 템플릿에 없는 dataDefs/dataSources를 이식하는 데 사용한다.
 *
 * **버전**(결정 2026-07-22 §payload v2):
 *   - v1 — 노드 축만. `widgets` 없음.
 *   - v2 — 작성 탭 위젯을 함께 싣는다(파생 위젯의 근거 body 노드는 `nodes`에 동봉).
 * 파서는 **1·2 모두 수용**하고 v1 입력의 `widgets`를 빈 배열로 보정하므로, 소비처는 버전으로 분기하지
 * 않고 `widgets`를 항상 배열로 다루면 된다.
 */
export interface NodeClipboardPayload {
  version: 1 | 2;
  nodes: ComposerNode[];
  /**
   * 복사한 작성 탭 위젯(v2에서 추가). v1 payload에서 파싱되면 빈 배열로 보정된다.
   * 파생 위젯이 기대는 body 노드는 별도 필드가 아니라 `nodes`에 동봉된다(단일 payload 통합 원칙).
   */
  widgets: ComposerWidget[];
  dataDefs: DataDef[];
  dataSources: DataSourceDef[];
  sourceTemplateKey: string;
  /**
   * 템플릿 레벨 결재라인 구조 — 복사 노드에 결재란(approvalBlock)이 포함될 때만 실린다.
   * 붙여넣기 대상에 기존 결재 설정이 없을 때만 이식(applyPasteApprovalMeta 정책).
   */
  approvalLineMeta?: ReportTemplate['approvalLineMeta'];
  /**
   * 템플릿 레벨 결재란 배치(approvalBlock 노드 id 참조) — 복사 노드에 결재란이 포함될 때만 실린다.
   * 붙여넣기 시 nodeId는 새로 재발급된 노드 id로 remap한다(EditorPage handlePasteNodes).
   */
  approvalPlacement?: ApprovalPlacement;
}

/**
 * 노드·위젯 payload의 공통 조립부(버전 결정 + 결재 메타 수집).
 *
 * **버전 정책: `widgets`가 비어 있으면 v1, 있을 때만 v2로 emit한다.**
 * 근거 — v2를 항상 emit하면, 아직 P2가 배포되지 않은 **구버전 admin**(파서가 `version !== 1` 거부)이
 * 새 클립보드 텍스트를 못 읽어 **기존 노드 복사/붙여넣기가 회귀**한다. 위젯이 없는 payload는 v1과
 * 내용이 완전히 동일하므로 v1을 유지하는 편이 무손실이고, 신·구 버전이 섞인 환경에서도 노드 축이 계속
 * 상호운용된다(dev↔prod, 다른 PC — 이 줄기의 목적 자체가 환경 간 이식이다).
 *
 * 결재 메타는 기존 규약 그대로 — **동봉 노드 트리에 approvalBlock이 있을 때만** 싣는다.
 */
function assembleClipboardPayload(
  nodes: ComposerNode[],
  widgets: ComposerWidget[],
  dataDefs: DataDef[],
  dataSources: DataSourceDef[],
  template: Pick<ReportTemplate, 'key' | 'approvalLineMeta' | 'approvalPlacement'>,
): NodeClipboardPayload {
  const payload: NodeClipboardPayload = {
    version: widgets.length > 0 ? 2 : 1,
    nodes,
    widgets,
    dataDefs,
    dataSources,
    sourceTemplateKey: template.key,
  };
  // 복사 노드에 결재란(approvalBlock)이 포함될 때만 템플릿 레벨 결재 메타를 함께 싣는다(값 있을 때만).
  if (treeHasApprovalBlock(nodes)) {
    if (template.approvalLineMeta) payload.approvalLineMeta = template.approvalLineMeta;
    if (template.approvalPlacement) payload.approvalPlacement = template.approvalPlacement;
  }
  return payload;
}

/**
 * 선택 노드들 + 그들이 참조하는 템플릿 스코프 의존 소스로 클립보드 payload를 구성한다(복사측 순수 op).
 *
 * `nodes`는 선택 노드 배열을 **그대로** 담는다(deep copy 불요 — 직렬화가 복제하고, 붙여넣기측
 * cloneNodesWithNewIds가 새 트리를 만든다). 의존 소스는 collectNodeDependencies로 수집하고,
 * sourceTemplateKey에 출처 템플릿 key를 담아 붙여넣기 시 같은/다른 템플릿 분기(isSameTemplatePaste)에
 * 쓴다. 노드만 복사하므로 `widgets`는 빈 배열 → 위 버전 정책에 따라 **v1로 emit**된다(구버전 호환).
 */
export function buildNodeClipboardPayload(
  nodes: ComposerNode[],
  template: Pick<
    ReportTemplate,
    'key' | 'dataDefs' | 'dataSources' | 'approvalLineMeta' | 'approvalPlacement'
  >,
): NodeClipboardPayload {
  const { dataDefs, dataSources } = collectNodeDependencies(nodes, template);
  return assembleClipboardPayload(nodes, [], dataDefs, dataSources, template);
}

/** payload를 localStorage/clipboard에 담을 문자열로 직렬화한다. */
export function serializeNodeClipboard(payload: NodeClipboardPayload): string {
  return JSON.stringify(payload);
}

/**
 * 클립보드 원소(노드·위젯)의 최소 shape 검증 — `id`/`kind`가 문자열인 객체인가.
 * ComposerNode/ComposerWidget 모두 이 두 필드를 판별자로 갖는 discriminated union이라 같은 술어를 쓴다
 * (검증 규칙을 축마다 복제하지 않는다).
 */
function hasMinimalShape(value: unknown): boolean {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as { id?: unknown }).id === 'string' &&
    typeof (value as { kind?: unknown }).kind === 'string'
  );
}

/**
 * 직렬화 문자열을 payload로 역직렬화한다(방어적 검증).
 *
 * JSON.parse 실패, null/빈 입력, version이 1·2가 아님, nodes가 배열이 아닌 경우 null을 반환한다.
 * dataDefs/dataSources/widgets가 배열이 아니면 빈 배열로 보정한다(구버전·부분 payload 관용).
 *
 * **v1 하위호환**: v1 payload(`widgets` 없음)는 `widgets: []`로 보정해 반환하므로, 소비처는 버전으로
 * 분기하지 않고 v2 형태 하나만 다루면 된다. 단 반환 `version`은 **입력 버전을 보존**한다(1은 1로) —
 * 정규화 대상은 *필드 shape*이지 payload의 출처가 아니고, version을 올려 반환하면 "구 클립보드에서 온
 * 것"이라는 사실이 사라져 재직렬화 시 v1 텍스트가 조용히 v2로 승격된다(구버전 admin이 못 읽게 됨 —
 * 위 buildNodeClipboardPayload 버전 정책과 같은 근거). 소비처가 version을 볼 필요는 없다.
 *
 * ⚠️ 붙여넣기 payload는 crossorigin OS 클립보드(NODE_CLIPBOARD_TEXT_PREFIX)에서도 오므로 신뢰할 수
 * 없다 — 각 노드·위젯의 최소 shape(`id`/`kind`가 문자열)를 검증해, 변조·형태 깨진 payload가
 * cloneNodesWithNewIds/렌더 단계에서 크래시하는 것을 사전 차단한다(하나라도 어긋나면 payload 거부).
 */
export function parseNodeClipboard(raw: string | null): NodeClipboardPayload | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;
  if (obj.version !== 1 && obj.version !== 2) return null;
  if (!Array.isArray(obj.nodes)) return null;
  if (!obj.nodes.every(hasMinimalShape)) return null;
  // widgets는 v1에 없으므로 **비배열이면 빈 배열로 관용 보정**(dataDefs/dataSources와 동일 정책).
  // 단 배열이면 원소 shape는 노드와 **같은 엄격도**로 검증한다(하나라도 불량이면 payload 거부).
  const widgets = Array.isArray(obj.widgets) ? obj.widgets : [];
  if (!widgets.every(hasMinimalShape)) return null;
  // 결재 메타는 옵셔널 — 객체면 방어적으로 통과, 아니면 생략(형 심층검증은 소비측 정책이 no-op으로 흡수).
  const approvalLineMeta =
    typeof obj.approvalLineMeta === 'object' && obj.approvalLineMeta !== null
      ? (obj.approvalLineMeta as NodeClipboardPayload['approvalLineMeta'])
      : undefined;
  const approvalPlacement =
    typeof obj.approvalPlacement === 'object' && obj.approvalPlacement !== null
      ? (obj.approvalPlacement as ApprovalPlacement)
      : undefined;
  return {
    version: obj.version === 2 ? 2 : 1, // 입력 버전 보존(위 주석 근거 — 위 guard로 1|2만 도달)
    nodes: obj.nodes as ComposerNode[],
    widgets: widgets as ComposerWidget[],
    dataDefs: Array.isArray(obj.dataDefs) ? (obj.dataDefs as DataDef[]) : [],
    dataSources: Array.isArray(obj.dataSources) ? (obj.dataSources as DataSourceDef[]) : [],
    sourceTemplateKey: typeof obj.sourceTemplateKey === 'string' ? obj.sourceTemplateKey : '',
    ...(approvalLineMeta !== undefined ? { approvalLineMeta } : {}),
    ...(approvalPlacement !== undefined ? { approvalPlacement } : {}),
  };
}

/**
 * payload 출처 템플릿이 붙여넣기 대상 템플릿과 같은지 판정한다(P3 분기 근거).
 *
 * 같은 템플릿이면 노드 트리를 즉시 삽입해도 의존 소스가 이미 있어 안전하고, 다른 템플릿이면
 * dataDefs/dataSources 이식 여부·충돌을 사용자에게 확인하는 모달을 띄우는 분기 근거가 된다.
 */
export function isSameTemplatePaste(
  payload: NodeClipboardPayload,
  targetTemplateKey: string,
): boolean {
  return payload.sourceTemplateKey === targetTemplateKey;
}

/**
 * payload의 의존 소스(dataDefs/dataSources)를 대상 template에 병합한 새 배열을 반환한다(원본 불변).
 *
 * 병합 규칙(결정기록 2026-07-06-composer-node-copy-paste "key 충돌 병합"):
 *   - payload의 각 def/source에 대해, 같은 key가 이미 template에 있으면 **내용 비교 없이 skip**하여
 *     기존 연결을 재사용한다. 없으면 append한다.
 *   - dataDefs와 dataSources는 서로 다른 배열이므로 각각 독립적으로 병합한다.
 *   - template.dataDefs/dataSources가 undefined면 빈 배열을 기준으로 병합한다.
 *
 * **dataDef lockstep**(P4, 결정 2026-08-07 §결론 4): `bindingKeyMap`(위젯 클론의 재발급 맵 채널
 * 그대로)을 받으면, payload dataDef 중 key가 재발급된 것(old≠new)은 **같은 새 key로 복제해**
 * 병합한다 — 위젯 bindingKey만 `-2`로 바뀌고 dataDef가 옛 key에 skip으로 남으면 표 위젯이 고아가
 * 된다("바인딩 삭제됨" 2026-08-07 실사고). 맵 미제공(v1 노드 붙여넣기 경로)·항등 매핑이면 기존
 * 규칙 그대로다. dataSources에는 적용하지 않는다 — 그 참조 축(repeat.over 등)은 클론이 재발급하지
 * 않아 맵과 교차할 일이 없다.
 *
 * 반환: 병합된 전체 배열(dataDefs/dataSources) + 실제로 새로 추가된 key 목록(addedDefKeys/addedSourceKeys,
 * 없으면 빈 배열 — lockstep 복제분은 **새 key**로 담긴다). 원본 template 배열은 mutate하지 않는다(spread 복제).
 */
export function mergeClipboardDependencies(
  template: Pick<ReportTemplate, 'dataDefs' | 'dataSources'>,
  payload: NodeClipboardPayload,
  bindingKeyMap?: ReadonlyMap<string, string>,
): {
  dataDefs: DataDef[];
  dataSources: DataSourceDef[];
  addedDefKeys: string[];
  addedSourceKeys: string[];
} {
  const dataDefs: DataDef[] = [...(template.dataDefs ?? [])];
  const dataSources: DataSourceDef[] = [...(template.dataSources ?? [])];
  const addedDefKeys: string[] = [];
  const addedSourceKeys: string[] = [];

  const existingDefKeys = new Set(dataDefs.map((d) => d.key));
  for (const def of payload.dataDefs) {
    // lockstep — 위젯 클론이 이 def의 key를 재발급했으면 def도 같은 새 key로 복제해 함께 이동한다
    // (structuredClone: payload 불변 유지 — 병합 결과가 payload 객체를 공유하면 이후 갱신이 원본을 오염).
    const mappedKey = bindingKeyMap?.get(def.key);
    const effective =
      mappedKey !== undefined && mappedKey !== def.key
        ? { ...structuredClone(def), key: mappedKey }
        : def;
    if (existingDefKeys.has(effective.key)) continue;
    existingDefKeys.add(effective.key);
    dataDefs.push(effective);
    addedDefKeys.push(effective.key);
  }

  const existingSourceKeys = new Set(dataSources.map((s) => s.key));
  for (const source of payload.dataSources) {
    if (existingSourceKeys.has(source.key)) continue;
    existingSourceKeys.add(source.key);
    dataSources.push(source);
    addedSourceKeys.push(source.key);
  }

  return { dataDefs, dataSources, addedDefKeys, addedSourceKeys };
}

/**
 * 붙여넣기 시 대상 template에 payload의 의존 소스만 주입한 새 template을 반환한다(body는 불변).
 *
 * mergeClipboardDependencies 결과로 `{ ...template, dataDefs, dataSources }`를 만든다. 단 원래 template의
 * 형태를 보존하기 위해, 각 필드가 원래 undefined였고 추가분도 0이면 그 키를 세팅하지 않는다(undefined 유지).
 * 노드 클론·본문 삽입은 같은 파일의 `applyWidgetClipboardPaste`가 담당하므로 여기서는 body를 손대지 않는다.
 *
 * `bindingKeyMap` — 위젯 붙여넣기 경로가 applyWidgetClipboardPaste 결과의 재발급 맵을 넘겨 dataDef
 * lockstep을 실제 템플릿 반영에도 적용한다(mergeClipboardDependencies 규칙 참조). 계산(result.dataDefs)과
 * 반영(이 함수)이 **같은 맵**을 써야 리포트의 addedDefKeys와 실제 병합이 어긋나지 않는다.
 * v1 노드 붙여넣기 경로는 미제공(기존 동작 그대로).
 */
export function applyPasteDependencies(
  template: ReportTemplate,
  payload: NodeClipboardPayload,
  bindingKeyMap?: ReadonlyMap<string, string>,
): ReportTemplate {
  const { dataDefs, dataSources, addedDefKeys, addedSourceKeys } =
    mergeClipboardDependencies(template, payload, bindingKeyMap);

  const next: ReportTemplate = { ...template };

  if (template.dataDefs !== undefined || addedDefKeys.length > 0) {
    next.dataDefs = dataDefs;
  }
  if (template.dataSources !== undefined || addedSourceKeys.length > 0) {
    next.dataSources = dataSources;
  }

  return next;
}

/**
 * 붙여넣기 시 payload의 템플릿 레벨 결재 메타를 대상 template에 이식한 새 template을 반환한다(원본 불변).
 *
 * 정책(사용자 확정): **대상에 기존 결재 설정이 없을 때만** 적용한다. 결재라인 구조(approvalLineMeta)와
 * 배치(approvalPlacement)는 배치가 라인을 참조하는 한 쌍이므로 **all-or-nothing(coupled)**으로 다룬다 —
 * 대상에 둘 중 하나라도 이미 있으면 **둘 다 미적용**(대상 라인 + 소스 배치가 섞이는 혼합을 방지).
 *   - 대상에 결재 설정이 전혀 없을 때만: payload.approvalLineMeta + remappedPlacement를 각각 있으면 세팅.
 *   - remappedPlacement는 EditorPage가 클론 시 재발급한 노드 id로 nodeId를 remap한 값이다(매핑 실패 시
 *     드롭되어 undefined). 원본 payload.approvalPlacement 아님.
 * 같은 템플릿 붙여넣기는 대상에 이미 결재 설정이 있어 자동 no-op이 된다(정상). 원본 template mutate 없음.
 */
export function applyPasteApprovalMeta(
  template: ReportTemplate,
  payload: NodeClipboardPayload,
  remappedPlacement: ApprovalPlacement | undefined,
): ReportTemplate {
  // 대상에 결재 설정이 하나라도 있으면 미적용(부분 혼합 방지 — coupled).
  const targetHasApproval =
    template.approvalLineMeta !== undefined || template.approvalPlacement !== undefined;
  if (targetHasApproval) return { ...template };

  const next: ReportTemplate = { ...template };
  if (payload.approvalLineMeta !== undefined) next.approvalLineMeta = payload.approvalLineMeta;
  if (remappedPlacement !== undefined) next.approvalPlacement = remappedPlacement;
  return next;
}

// ============================================================
// 위젯 축 의존 수집(P1) — 작성 탭 위젯을 복사할 때 따라와야 하는 것들
//
// 노드 축(collectOverKeys/collectNodeDependencies)은 `over` 하나로 끝나지만, 위젯은 두 갈래를 본다:
//   ① 템플릿 스코프 데이터 키 — optionsSources / repeat.over / formTable 반복 행 sourceKey.
//      → dataDefs/dataSources 매칭은 노드 축과 **완전히 같은 규칙**(collectNodeDependencies 재사용).
//   ② body 바인딩 참조 — 파생 input의 bindingKey. 이 위젯은 대응 body 노드 없이는 존재할 수 없으므로
//      그 바인딩을 소유한 body 노드를 **역인덱스로 찾아 동봉**한다(결정 2026-07-22 §복사 시 의존 수집).
//
// ⚠️ 수집 제외(노드 축과 동일 근거 — 템플릿이 아니라 **대상 환경**의 자산이라 payload로 옮길 수 없음):
//    `prefillFrom.catalogKey` / `optionsFrom.catalogKey` / `submitAction.catalogKey` / `widgetKey` /
//    `presetRef`. 붙여넣기 리포트의 "미해소" 항목으로 드러내는 것이 정본(P5).
// ============================================================

/**
 * 위젯 트리를 재귀 순회하며 **템플릿 스코프 데이터 키**를 수집한다(중복 제거, 삽입 순서 보존, 빈 문자열 제외).
 *
 * 수집 축(shared 타입 전수 확인 결과):
 *   - InputWidget: effectiveOptionsSources(복수 `optionsSources` 우선, 없으면 deprecated 단수
 *     `optionsSource` 1원소 — rc 헬퍼 재사용으로 규약 이원화 금지)
 *   - InputWidget(widget:'table')의 `bindingKey` — 내장 데이터셋 표는 bindingKey가 곧 DataDef.key다
 *     (deriveWriteForm N2-A 파생 규약). 이 축이 없으면 표 위젯 단독 복사 payload에 dataDef가 안 실려,
 *     대상에 정본이 없을 때 이식할 근거가 없다(2026-08-07 고아 실사고 계열 — P4 lockstep의 전제).
 *     dataDef 없는 표(카탈로그 스캐폴드 등)의 bindingKey는 아래 dataDefs/dataSources 매칭에서
 *     자연히 무시된다(collectNodeDependencies 관용과 동일).
 *   - RepeatWidget.over (편집형 DataSourceDef.key)
 *   - FormTableRow(type:'repeat').sourceKey (편집형 DataSourceDef.key)
 * 순회 경계는 rc cloneWidgetDeep/remapPresetRefsInPlace와 동일:
 *   group → children, branch → branches[].children, modalTrigger → children, repeat → children,
 *   formTable → 고정 행 셀의 widget.
 * modalTrigger/repeat children까지 하강하는 이유: 모달·행 스코프는 **값 스코프**일 뿐이고 소스 키는
 * 스코프 무관 전역이다(rc collectSortableSelectOptionSources와 동일 판단).
 *
 * ⚠️ FormTableFixedCell에는 `aggregate` 축이 없다(양식 탭 TableCell 전용) — 셀 계산은 `compute`이고,
 *    그 `inputs[].binding.bindingKey`는 템플릿 데이터 키가 아니라 **바인딩 참조**라 이 함수가 아니라
 *    collectWidgetBindingKeys(body 노드 역인덱스 축)가 수집한다.
 */
export function collectWidgetOverKeys(widgets: ComposerWidget[]): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();

  const add = (key: string | undefined) => {
    if (!key) return;
    if (seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  };

  const addInput = (w: InputWidget) => {
    if (w.widget === 'table') add(w.bindingKey); // 내장 데이터셋 표 — bindingKey = DataDef.key
    for (const k of effectiveOptionsSources(w)) add(k);
  };

  const walk = (list: ComposerWidget[]) => {
    for (const w of list) {
      if (w.kind === 'input') {
        addInput(w);
      } else if (w.kind === 'group' || w.kind === 'modalTrigger') {
        walk(w.children);
      } else if (w.kind === 'repeat') {
        add(w.over);
        walk(w.children);
      } else if (w.kind === 'branch') {
        for (const slot of w.branches) walk(slot.children);
      } else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type === 'repeat') {
            add(row.sourceKey);
            continue;
          }
          for (const cell of row.cells) {
            if (cell.widget) addInput(cell.widget);
          }
        }
      }
    }
  };

  walk(widgets);
  return keys;
}

/**
 * 위젯 트리에서 **body 바인딩 참조 키**를 수집한다(중복 제거, 삽입 순서 보존, 빈 문자열 제외).
 * collectWidgetBodyNodes의 역인덱스 조회 키가 된다.
 *
 * 수집 축:
 *   - 파생 input(비-treeOwned·비-registered)의 `bindingKey` — 이 위젯의 존재 근거인 body 노드 참조.
 *   - formTable 고정 행의 **파생 셀 위젯** `bindingKey`(같은 근거).
 *   - formTable 고정 행 셀 `compute.inputs[].binding.bindingKey` — 계산 셀이 읽는 값의 출처.
 *
 * ⚠️ 순회 경계가 collectWidgetOverKeys와 **다르다** — modalTrigger children(모달 스코프)과 repeat
 *    children(행 필드 상대키)은 하강하지 않는다. 그 스코프의 bindingKey는 body 바인딩이 아니라서
 *    (rc cloneWidgetDeep이 '-copy' 재발급 없이 보존하는 이유와 같은 근거) 역인덱스에 태우면 동명이인
 *    body 노드를 잘못 동봉한다. 경계는 rc countDerivedInputsExcludedOnClone과 정확히 일치한다.
 * ⚠️ modalTrigger 자신의 `bindingKey`는 모달 UI 상태를 담는 트리 소유 키(파생 아님)라 제외한다.
 */
export function collectWidgetBindingKeys(widgets: ComposerWidget[]): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();

  const add = (key: string | undefined) => {
    if (!key) return;
    if (seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  };

  const walk = (list: ComposerWidget[]) => {
    for (const w of list) {
      if (w.kind === 'input') {
        if (isDerivedInput(w)) add(w.bindingKey);
      } else if (w.kind === 'group') {
        walk(w.children);
      } else if (w.kind === 'branch') {
        // 투명 컨테이너(부모 값 스코프) — group과 동일하게 하강.
        for (const slot of w.branches) walk(slot.children);
      } else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) {
            if (cell.widget && isDerivedInput(cell.widget)) add(cell.widget.bindingKey);
            for (const input of cell.compute?.inputs ?? []) add(input.binding?.bindingKey);
          }
        }
      }
      // modalTrigger(모달 스코프)·repeat(행 스코프) children은 body 바인딩이 아니라 하강하지 않는다.
    }
  };

  walk(widgets);
  return keys;
}

/**
 * body 트리의 `bindingKey → 그 바인딩을 소유한 노드` 역인덱스를 만든다(같은 키 중복 시 **먼저 만난 노드**).
 *
 * **순회·판정 모두 rc 정본을 재사용한다** — 평행 순회를 손으로 짜면 규칙이 갈라져(예: repeat 행 상대키가
 * 트리 순서상 앞서면 같은 키의 진짜 소유자를 가림) 엉뚱한 노드를 동봉한다:
 *   - 순회: `walkBindings`(rc) — deriveWriteForm과 같은 순서/구조.
 *   - 파생 대상 판정: `createWriteFormBindingFilter`(rc) — computeInput/conditionBinding/
 *     repeatRegion 밴드 셀/repeat 서브트리/밴드 셀 content 제외. **파생 input 위젯의 bindingKey는
 *     이 필터를 통과한 바인딩에서만 나오므로**, 제외 대상을 인덱싱하면 오탐만 늘어난다.
 *
 * 반환 노드 단위:
 *   - 셀 바인딩(source:'cell') → 그 셀이 속한 **table 노드**(셀은 노드가 아니라 단독 이식 불가).
 *   - 노드 바인딩(source:'node') → 바인딩을 직접 가진 **노드 자신**(group/stack/branch는 투명
 *     컨테이너 — 묶음 전체를 끌고 오면 무관한 형제까지 이식돼 붙여넣기가 과잉이 된다).
 */
function buildBodyBindingIndex(body: ComposerNode[]): Map<string, ComposerNode> {
  const index = new Map<string, ComposerNode>();
  const isWriteFormBinding = createWriteFormBindingFilter(body);
  const tables = buildTableIndex(body);

  walkBindings(body, (binding, owner) => {
    if (!isWriteFormBinding(owner)) return;
    const node = owner.source === 'cell' && owner.cell ? tables.get(owner.cell.tableId) : owner.node;
    if (!node) return;
    if (!binding.bindingKey) return;
    if (index.has(binding.bindingKey)) return; // 먼저 만난 소유자 승리(트리 순서)
    index.set(binding.bindingKey, node);
  });

  return index;
}

/**
 * 위젯이 파생된 **body 노드**를 역인덱스로 찾아 반환한다(중복 제거는 노드 id 기준, 삽입 순서 보존).
 *
 * collectWidgetBindingKeys가 모은 바인딩 참조 키를 buildBodyBindingIndex로 조회한다.
 *   - `treeOwned`/등록형 위젯은 body 대응물이 없으므로 애초에 조회 대상이 아니다.
 *   - 매칭되는 노드가 없는 키(변수/dataDef 파생, 반복 행 상대키, 조건/계산 전용 키 등)는 **무시**한다 —
 *     노드 축이 미매칭 over를 무시하는 것과 같은 관용(collectNodeDependencies 규약). 인덱스가
 *     deriveWriteForm 제외 규칙(rc 정본)을 그대로 쓰므로, 파생 위젯이 없는 바인딩은 애초에 조회되지 않는다.
 */
export function collectWidgetBodyNodes(
  widgets: ComposerWidget[],
  template: Pick<ReportTemplate, 'body'>,
): ComposerNode[] {
  const index = buildBodyBindingIndex(template.body ?? []);
  const nodes: ComposerNode[] = [];
  const seenIds = new Set<string>();

  for (const key of collectWidgetBindingKeys(widgets)) {
    const node = index.get(key);
    if (!node) continue;
    if (seenIds.has(node.id)) continue;
    seenIds.add(node.id);
    nodes.push(node);
  }

  return nodes;
}

/**
 * 위젯 복사의 전체 의존 묶음 — 동봉할 body 노드 + 템플릿 스코프 데이터 소스.
 *
 * 수집 순서:
 *   ① collectWidgetBodyNodes — 파생 위젯이 기대는 body 노드.
 *   ② 위젯 자신의 템플릿 스코프 키(collectWidgetOverKeys) **∪ 동봉 노드의 own over 키**
 *      (collectOverKeys) — 표를 동봉했는데 그 표가 repeatRegion으로 dataDef를 참조하면 그 def도
 *      함께 가야 한다. 합집합은 위젯 키를 먼저 두어 삽입 순서를 안정화한다.
 *   ③ dataDefs/dataSources 매칭은 노드 축과 동일 규칙(dataSources 우선, 미매칭 무시) —
 *      collectNodeDependencies에 넘길 수 없는 형태(키 목록)라 같은 규칙을 여기서 적용한다.
 */
export function collectWidgetDependencies(
  widgets: ComposerWidget[],
  template: Pick<ReportTemplate, 'body' | 'dataDefs' | 'dataSources'>,
): { nodes: ComposerNode[]; dataDefs: DataDef[]; dataSources: DataSourceDef[] } {
  const nodes = collectWidgetBodyNodes(widgets, template);

  const keys: string[] = [];
  const seen = new Set<string>();
  for (const key of [...collectWidgetOverKeys(widgets), ...collectOverKeys(nodes)]) {
    if (seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }

  const dataDefs: DataDef[] = [];
  const dataSources: DataSourceDef[] = [];
  for (const key of keys) {
    const source = template.dataSources?.find((s) => s.key === key);
    if (source) {
      dataSources.push(source);
      continue;
    }
    const def = template.dataDefs?.find((d) => d.key === key);
    if (def) dataDefs.push(def);
  }

  return { nodes, dataDefs, dataSources };
}

/**
 * 선택 위젯들 + 동봉 body 노드 + 의존 데이터 소스로 클립보드 payload를 구성한다(복사측 순수 op, P2).
 *
 * 노드 축 buildNodeClipboardPayload와 **같은 payload 타입**을 만든다(결정 2026-07-22 §단일 payload 통합) —
 * 위젯 payload가 근거 body 노드를 함께 실어야 붙여넣기 시 노드 id ↔ 위젯 바인딩 재매핑이 한 트랜잭션
 * 안에서 일어난다(P3).
 *   - `nodes` — collectWidgetDependencies가 역인덱스로 찾은 **동봉 body 노드**(위젯이 파생된 근거).
 *     선택 위젯이 전부 treeOwned/등록형이면 빈 배열일 수 있다(정상 — 대응 body 노드가 없는 위젯).
 *   - `widgets` — 선택 위젯 배열을 **그대로** 담는다(노드 축과 동일 — 복제는 직렬화/붙여넣기 몫).
 *   - 결재 메타는 기존 규약 그대로 **동봉 노드 트리에 approvalBlock이 있을 때만** 싣는다.
 *   - 위젯이 하나라도 있으면 v2로 emit된다(assembleClipboardPayload 버전 정책).
 */
export function buildWidgetClipboardPayload(
  widgets: ComposerWidget[],
  template: Pick<
    ReportTemplate,
    'key' | 'body' | 'dataDefs' | 'dataSources' | 'approvalLineMeta' | 'approvalPlacement'
  >,
): NodeClipboardPayload {
  const { nodes, dataDefs, dataSources } = collectWidgetDependencies(widgets, template);
  return assembleClipboardPayload(nodes, widgets, dataDefs, dataSources, template);
}

// ============================================================
// 붙여넣기 — 클론·lockstep 키 재발급·멱등 병합
//
// DOM·React state·undo 스텝·모달 표시는 하지 않는다(EditorPage 몫). 다만 **완전한 순수 함수는
// 아니다**: 입력 payload/템플릿은 불변으로 다루지만, 주입받은 id 생성 컨텍스트(`nodeIdCounter`·
// `existingNodeIds`)는 호출 간 유일성 보장을 위해 **의도적으로 누적 변형**하고, 자기가 만든 클론
// 노드/위젯은 제자리 수정한다(원본 무접촉).
// 재매핑 엔진은 rc `clonePresetSubtreeForInsert` **재사용**(신규 작성 금지 — 결정 2026-07-22).
// ============================================================

/** 붙여넣기 대상 컨텍스트 — 대상 템플릿 + 작성 트리 + 이미 사용 중인 bindingKey 집합. */
export interface WidgetClipboardPasteTarget {
  /** 대상 템플릿(body = 멱등 병합 판정 기준, dataDefs/dataSources = 의존 병합 기준). */
  template: ReportTemplate;
  /** 대상 작성 트리(배치된 위젯 키 판정 — collectPlacedInputKeys). */
  widgetTree: ComposerWidget[];
  /**
   * 새 위젯 키가 피해야 할 키 전체(배치 + 미배치 풀 + 양식 — duplicateComposerWidget과 동일 계약).
   * 이 집합에서 "대상 body 노드가 소유하지만 아직 배치되지 않은 키"는 제외되어(아래 면제 규칙)
   * 동봉 노드가 재사용될 때 위젯이 원 키를 유지한다.
   */
  takenBindingKeys: Set<string>;
}

/** 클론 plumbing — id 생성기/카운터(테스트 결정성 위해 주입). */
export interface WidgetClipboardPasteOptions {
  /** 위젯 id 생성기(EditorPage는 crypto.randomUUID). */
  newWidgetId: () => string;
  /** 대상 body에 이미 존재하는 노드 id(클론 id 충돌 회피 — cloneNodesWithNewIds 계약). */
  existingNodeIds: Set<string>;
  /** 노드 id 연번 카운터(EditorPage idCounter ref). */
  nodeIdCounter: { current: number };
}

/**
 * 붙여넣기 계산 결과 — 삽입할 위젯/노드 + 병합된 의존 데이터 + 3분류 리포트 근거(P5 입력).
 */
export interface WidgetClipboardPasteResult {
  /** 삽입할 위젯(id 재발급 + 내부 참조 재매핑 완료). */
  widgets: ComposerWidget[];
  /** 새로 삽입할 body 노드(대상에 이미 있어 재사용된 것은 제외 — 멱등). id 재발급 + lockstep 키 적용. */
  nodes: ComposerNode[];
  /** 삽입 노드의 old→new id 매핑(결재란 배치 nodeId remap 등 참조 재매핑용 — handlePasteNodes 동형). */
  nodeIdMap: Map<string, string>;
  /**
   * 병합된 **전체** dataDefs/dataSources(mergeClipboardDependencies 규칙 — 같은 key면 기존 재사용,
   * 단 key가 재발급된 payload dataDef는 같은 새 key로 복제되어 담긴다: lockstep P4).
   */
  dataDefs: DataDef[];
  dataSources: DataSourceDef[];
  /** 실제로 새로 추가된 데이터 키(리포트 "이식됨" 항목). */
  addedDefKeys: string[];
  addedSourceKeys: string[];
  /** 위젯 클론이 재발급한 bindingKey 매핑(old→new). 재발급이 없으면 old===new. */
  bindingKeyMap: Map<string, string>;
  /** 대상에 이미 같은 키의 노드가 있어 **재사용**한 바인딩 키(리포트 "기존 재사용"). */
  reusedNodeKeys: string[];
  /** 새 노드로 이식된 바인딩 키(리포트 "이식됨"). */
  portedNodeKeys: string[];
  /** 묶음 밖 참조 — 재매핑 못 한 것(리포트 "미해소"). rc clonePresetSubtreeForInsert 보고분. */
  unresolvedRefs: PresetUnresolvedRef[];
}

/**
 * 노드 묶음에서 `노드 id → 그 노드가 소유한 body 바인딩 키 목록`을 만든다.
 *
 * buildBodyBindingIndex(정본 술어 재사용 — createWriteFormBindingFilter/walkBindings)의 결과를 **뒤집는다**.
 * 판정 규칙을 여기서 다시 짜지 않는 것이 핵심이다(P1 CRITICAL: 평행 순회는 제외 규칙이 갈라진다).
 * 같은 키를 여러 노드가 가지면 인덱스가 먼저 만난 소유자만 담으므로 여기서도 그 소유자에게만 귀속된다.
 */
function collectOwnedBindingKeysByNode(nodes: ComposerNode[]): Map<string, string[]> {
  const byNode = new Map<string, string[]>();
  for (const [key, node] of buildBodyBindingIndex(nodes)) {
    const list = byNode.get(node.id);
    if (list) list.push(key);
    else byNode.set(node.id, [key]);
  }
  return byNode;
}

/**
 * **lockstep 키 재발급** — 클론된 노드들의 binding.bindingKey를 위젯 클론이 만든 매핑으로 갱신한다.
 *
 * 위젯 클론이 `K → K-2`로 재발급했는데 노드가 `K`를 유지하면, 위젯이 가리키는 키를 소유한 노드가
 * 없어 **파생 관계가 끊긴 채** 붙는다(결정 2026-07-22 §경로 독립성 "✅ 3번 결론"). 그래서 함께 이식되는
 * 노드에만 같은 매핑을 적용한다 — 매핑이 비었거나 전부 항등이면 **완전 no-op**이라 기존 노드 단독
 * 복사 동작은 불변이다(회귀 범위가 좁은 것이 이 처방을 택한 이유).
 *
 * 순회·적용 경계는 buildBodyBindingIndex와 **같은 술어**(walkBindings + createWriteFormBindingFilter)다 —
 * 동봉 노드가 뽑힌 근거와 갱신 대상이 정확히 같은 집합이어야 하고, 제외 대상(repeat 서브트리 행 상대키·
 * repeatRegion 밴드 셀·계산 입력·조건 바인딩)까지 갱신하면 동명이인 키를 잘못 바꾸게 된다.
 *
 * 노드는 cloneNodesWithNewIds(structuredClone)가 만든 새 객체라 제자리 갱신이 원본을 건드리지 않는다.
 */
function applyBindingKeyMapToNodes(nodes: ComposerNode[], keyMap: Map<string, string>): void {
  if (keyMap.size === 0) return;
  let hasRename = false;
  for (const [oldKey, newKey] of keyMap) {
    if (oldKey !== newKey) {
      hasRename = true;
      break;
    }
  }
  if (!hasRename) return; // 무충돌(다른 템플릿) — 노드 바인딩 무접촉
  const isWriteFormBinding = createWriteFormBindingFilter(nodes);
  walkBindings(nodes, (binding, owner) => {
    if (!isWriteFormBinding(owner)) return;
    const mapped = keyMap.get(binding.bindingKey);
    if (mapped === undefined || mapped === binding.bindingKey) return;
    binding.bindingKey = mapped;
  });
}

/**
 * **dataDef lockstep의 over 축**(P4) — 이식 노드의 데이터셋 참조(repeat.over / table
 * repeatRegions[].over / cells[].aggregate.over)를 dataDef 재발급 맵으로 갱신한다.
 *
 * applyBindingKeyMapToNodes(바인딩 축)와 짝이다: 위젯 bindingKey 재발급으로 dataDef가 새 key로
 * 복제(lockstep)될 때, 함께 이식되는 양식 표/반복 노드가 옛 key를 계속 가리키면 대상의 **다른 내용**
 * dataDef를 읽는다(위젯 고아와 같은 계열의 조용한 어긋남). 맵은 payload dataDefs의 key로 제한된
 * 재발급 맵(defKeyMap)이라 dataSource·전역 카탈로그 over는 건드리지 않는다. 순회 경계는
 * collectOverKeys와 동일. 노드는 cloneNodesWithNewIds가 만든 새 객체라 제자리 갱신이 원본 무접촉.
 */
function applyDefKeyMapToNodeOvers(nodes: ComposerNode[], defKeyMap: Map<string, string>): void {
  if (defKeyMap.size === 0) return;
  const walk = (list: ComposerNode[]) => {
    for (const n of list) {
      if (n.kind === 'group' || n.kind === 'stack') {
        walk(n.children);
      } else if (n.kind === 'table') {
        for (const region of n.repeatRegions ?? []) {
          const mapped = defKeyMap.get(region.over);
          if (mapped !== undefined) region.over = mapped;
        }
        for (const cell of n.cells) {
          if (cell.aggregate) {
            const mapped = defKeyMap.get(cell.aggregate.over);
            if (mapped !== undefined) cell.aggregate.over = mapped;
          }
          if (cell.content) walk(cell.content);
        }
      } else if (n.kind === 'repeat') {
        const mapped = defKeyMap.get(n.over);
        if (mapped !== undefined) n.over = mapped;
        walk([n.template]);
      } else if (n.kind === 'branch') {
        for (const slot of n.branches) walk(slot.children);
      }
    }
  };
  walk(nodes);
}

/**
 * 위젯/노드 payload를 대상 템플릿에 붙여넣을 때의 **순수 계산**(P3 본체).
 *
 * 순서와 규칙:
 *  1. **키 면제** — 대상 body 노드가 소유하지만 대상 작성 트리에 **배치되지 않은** 키는 taken에서 뺀다.
 *     그 키의 노드는 아래 3에서 재사용될 대상이므로, 위젯이 원 키를 유지해야 그 노드에 붙는다.
 *     (같은 템플릿은 원 위젯이 이미 배치돼 있어 면제되지 않는다 → 재발급 → lockstep으로 노드도 복제.)
 *     이 면제가 경로 독립성(다른 템플릿 한정)의 성립 근거다 — "노드 먼저 붙여넣기" 경로에서 대상에
 *     생긴 노드가 다음 위젯 붙여넣기의 키 재발급을 유발하지 않는다.
 *     **dataDef 면제**(P4, 결정 2026-08-07 §결론 4)도 같은 자리: payload가 실어 온 dataDef와 같은
 *     key의 정본이 대상에 있고 **내용 전체 동일**(dataDefEqual)하며 그 키가 대상 트리에 미배치면,
 *     표 위젯이 기존 정본을 그대로 참조하도록 원 키를 유지한다(재발급하면 참조 고아). 대상 dataDef의
 *     파생 스캐폴드가 미배치 풀에 있는 것만으로 키가 taken이 되므로(collectPasteTakenKeys가
 *     mergedWriteForm 키 포함) 이 면제가 없으면 "같은 데이터셋 재이식"이 항상 fork가 된다.
 *  2. **위젯 클론** — rc clonePresetSubtreeForInsert를 위젯마다 순차 호출하며 **taken 집합을 공유**해
 *     (cloneWidgetsForInsert 동형) 상호 충돌을 막고, 키 매핑을 누적한다. `keepDerivedInputs: true` —
 *     이 경로는 근거 body 노드를 함께 이식하므로 프리셋과 달리 파생 input을 살린다.
 *  3. **멱등 병합** — payload 노드가 소유한 키를 **매핑 적용 후(=최종 키)** 대상 body와 대조해, 전부
 *     이미 있으면 새 노드를 만들지 않고 **재사용**한다(기존 우선 — 대상 노드가 편집됐을 수 있으므로
 *     덮어쓰지 않는다). 최종 키 기준인 이유: 재발급된 키(K-2)를 소유한 노드는 대상에 없으므로 반드시
 *     이식돼야 하고, 재발급이 없었으면 원 키로 대조하는 것이 곧 "이미 있는 노드"의 정의다.
 *     ⚠️ **위젯 없는 v1 payload는 병합을 적용하지 않는다** — 노드만 복사·붙여넣기는 "붙여넣은 만큼
 *     늘어난다"가 기존 계약이고(handlePasteNodes), 여기서 재사용으로 바꾸면 회귀다.
 *  4. **lockstep** — 이식 노드의 바인딩 키를 2의 매핑으로 갱신(applyBindingKeyMapToNodes). dataDef
 *     key가 재발급된 경우 이식 노드의 over 축(repeat/repeatRegion/aggregate)도 같은 맵으로 갱신
 *     (applyDefKeyMapToNodeOvers — 안 하면 함께 이식된 양식 표가 대상의 **다른 내용** dataDef를 읽는다).
 *  5. **의존 데이터** — mergeClipboardDependencies에 2의 재발급 맵을 넘긴다: 같은 key면 기존 재사용
 *     (내용 비교 없이)은 그대로, 단 **key가 재발급된 payload dataDef는 같은 새 key로 복제**돼 위젯과
 *     함께 이동한다(고아 방지 — 위젯·body·dataDef 세 곳이 같은 맵으로 움직인다).
 *
 * 결재 메타(applyPasteApprovalMeta)와 삽입 위치·undo는 이 함수 밖(P4)이다 — nodeIdMap을 돌려주므로
 * 호출처가 approvalPlacement remap을 기존과 동일하게 수행할 수 있다.
 */
export function applyWidgetClipboardPaste(
  payload: NodeClipboardPayload,
  target: WidgetClipboardPasteTarget,
  opts: WidgetClipboardPasteOptions,
): WidgetClipboardPasteResult {
  // 1. 대상 body가 소유한 파생 키 / 대상 트리에 배치된 위젯 키 → 면제 집합 산출.
  const targetNodeKeys = new Set(buildBodyBindingIndex(target.template.body ?? []).keys());
  const placedKeys = collectPlacedInputKeys(target.widgetTree);
  // dataDef 면제 근거 — payload dataDef와 같은 key + **내용 전체 동일**한 정본이 대상에 있는 키.
  // 내용이 다르면 면제하지 않는다(재발급 + 아래 5 lockstep 복제로 위젯·dataDef가 함께 새 키로 이동).
  const targetDefs = target.template.dataDefs ?? [];
  const equalDefKeys = new Set<string>();
  for (const def of payload.dataDefs) {
    const existing = targetDefs.find((d) => d.key === def.key);
    if (existing && dataDefEqual(def, existing)) equalDefKeys.add(def.key);
  }
  const taken = new Set<string>();
  for (const key of target.takenBindingKeys) {
    // 면제: 대상 노드가 소유 + 아직 미배치 → 붙여넣는 위젯이 그 노드에 붙도록 원 키 유지.
    if (targetNodeKeys.has(key) && !placedKeys.has(key)) continue;
    // 면제: 동일 내용 dataDef 정본이 대상에 존재 + 그 키의 표 위젯 미배치 → 기존 정본 재사용
    // (배치돼 있으면 body 노드 면제와 같은 근거로 면제 불가 — 같은 저장 키 위젯 2개는 비정상).
    if (equalDefKeys.has(key) && !placedKeys.has(key)) continue;
    taken.add(key);
  }

  // 2. 위젯 클론(taken 공유 누적) + 키 매핑/미해소 누적.
  const widgets: ComposerWidget[] = [];
  const bindingKeyMap = new Map<string, string>();
  const unresolvedRefs: PresetUnresolvedRef[] = [];
  for (const widget of payload.widgets) {
    const cloned = clonePresetSubtreeForInsert(widget, taken, opts.newWidgetId, {
      keepDerivedInputs: true,
    });
    widgets.push(cloned.widget);
    unresolvedRefs.push(...cloned.unresolvedRefs);
    for (const [oldKey, newKey] of cloned.bindingKeyMap) {
      // 같은 old 키가 두 위젯에 있으면 **먼저 배정된 매핑**을 유지한다(노드는 하나뿐 —
      // writeForm이 키로 dedup하므로 같은 키 위젯 2개는 애초에 정상 상태가 아니다).
      if (!bindingKeyMap.has(oldKey)) bindingKeyMap.set(oldKey, newKey);
    }
  }

  // 3. 멱등 병합 판정(위젯 동봉 payload만) — 최종 키 기준.
  const ownedByNode = collectOwnedBindingKeysByNode(payload.nodes);
  const mergeEnabled = payload.widgets.length > 0;
  const nodesToPort: ComposerNode[] = [];
  const reusedNodeKeys: string[] = [];
  const portedNodeKeys: string[] = [];
  const pushUnique = (list: string[], key: string) => {
    if (!list.includes(key)) list.push(key);
  };
  for (const node of payload.nodes) {
    const finalKeys = (ownedByNode.get(node.id) ?? []).map((k) => bindingKeyMap.get(k) ?? k);
    // 소유 키가 하나도 없는 노드(정적 노드 등)는 대조 근거가 없으므로 그대로 이식한다.
    const reusable =
      mergeEnabled && finalKeys.length > 0 && finalKeys.every((k) => targetNodeKeys.has(k));
    if (reusable) {
      for (const key of finalKeys) pushUnique(reusedNodeKeys, key);
      continue;
    }
    nodesToPort.push(node);
    for (const key of finalKeys) pushUnique(portedNodeKeys, key);
  }

  // 4. 노드 클론(id 재발급, 공유 existing/counter) + lockstep 키 갱신(바인딩 축 + dataDef over 축).
  const nodeIdMap = new Map<string, string>();
  const nodes = cloneNodesWithNewIds(
    nodesToPort,
    opts.existingNodeIds,
    opts.nodeIdCounter,
    nodeIdMap,
  );
  applyBindingKeyMapToNodes(nodes, bindingKeyMap);
  // dataDef lockstep 맵 — 재발급 맵(bindingKeyMap)을 payload dataDefs의 key로 제한한 부분집합.
  // over 축은 dataSource 키도 담는 축이라 전체 맵을 그대로 쓰면 무관 참조를 오염시킨다.
  const defKeyMap = new Map<string, string>();
  for (const def of payload.dataDefs) {
    const mapped = bindingKeyMap.get(def.key);
    if (mapped !== undefined && mapped !== def.key) defKeyMap.set(def.key, mapped);
  }
  applyDefKeyMapToNodeOvers(nodes, defKeyMap);

  // 5. 의존 데이터 병합 — 재발급 맵을 넘겨 dataDef lockstep 복제까지 한 규칙으로(고아 방지).
  const { dataDefs, dataSources, addedDefKeys, addedSourceKeys } = mergeClipboardDependencies(
    target.template,
    payload,
    bindingKeyMap,
  );

  return {
    widgets,
    nodes,
    nodeIdMap,
    dataDefs,
    dataSources,
    addedDefKeys,
    addedSourceKeys,
    bindingKeyMap,
    reusedNodeKeys,
    portedNodeKeys,
    unresolvedRefs,
  };
}

// ============================================================
// 이식 리포트(P5) — 3분류 산출 + 환경 자산 미해소 검출 + 표시 조건
//
// 붙여넣기가 **이미 적용된 뒤** 결과를 설명하는 보고용 순수 계산이다 — 여기의 어떤 함수도 붙여넣기
// 동작(삽입 위치·undo 스텝·cut 경로)을 바꾸지 않는다. 모달 컴포넌트는 이 산출물을 그리기만 한다
// (판정을 React에 묻지 않는다 — 조건이 컴포넌트 안에 숨으면 테스트로 고정할 수 없다).
//
// "미해소"는 두 갈래다:
//   ① 묶음 밖 위젯 참조 — rc clonePresetSubtreeForInsert가 보고하는 PresetUnresolvedRef.
//   ② **환경 자산** — payload로 옮길 수 없는 대상 환경의 것(결정 2026-07-22 §이식성의 경계):
//      등록형 위젯(widgetKey=코드 레지스트리) / 프리셋(presetRef=대상 DB 행) / 전역 카탈로그
//      (catalogKey 계열=서버 코드·DB) / 결재 메타(coupled 정책상 대상에 결재 설정이 있으면 미적용).
// ============================================================

/** 서버 합성 카탈로그 행 키(카탈로그 인덱스에 없어도 정상) — 조회용 Set 1회 생성. */
const SYNTHESIZED_CATALOG_KEYS: ReadonlySet<string> = new Set(SERVER_SYNTHESIZED_CATALOG_ROW_KEYS);

/** 미해소 환경 자산의 축 — 대상 환경에서 해소되지 않은 참조의 종류. */
export type WidgetPasteAssetAxis = 'widgetKey' | 'presetRef' | 'catalogKey' | 'approvalMeta';

/** 대상 환경에서 해소되지 않은 자산 참조 1건. */
export interface WidgetPasteUnresolvedAsset {
  axis: WidgetPasteAssetAxis;
  /**
   * 해소하지 못한 참조 값 — 등록형 위젯 키 / 프리셋 id / 카탈로그 entry 키 /
   * 결재 메타 필드명('approvalLineMeta' | 'approvalPlacement').
   */
  value: string;
  /** 참조를 보유한 위젯의 **새(재발급) id** — 위젯 축에서만 채워진다. */
  widgetId?: string;
}

/**
 * 붙여넣기 대상 **환경**이 보유한 자산 스냅샷(해소 판정 입력).
 *
 * 세 축 모두 EditorPage에 이미 있는 상태를 그대로 넘긴다(검출 규칙을 위해 새 조회를 만들지 않는다):
 *   - `catalogIndex` — `buildCatalogIndex(catalog)` 결과.
 *   - `presetIds` — `presetLibrary`(refreshPresetLibrary가 채우는 목록)의 id 집합.
 *   - `registeredWidgetKeys` — 미지정 시 **코드 레지스트리**(REGISTERED_FORM_WIDGETS_META). 등록형
 *     위젯의 가용성은 런타임 상태가 아니라 이 빌드의 코드 사실이라 기본값이 정본이다(테스트 주입용 override).
 */
export interface WidgetPasteEnvironment {
  catalogIndex: CatalogIndex;
  presetIds: ReadonlySet<string>;
  registeredWidgetKeys?: ReadonlySet<string>;
}

/** 미해소 자산 검출 대상 — 이번 붙여넣기로 **새로 들어온 것**만 스캔한다. */
export interface WidgetPasteAssetScanInput {
  /** 삽입된 위젯(클론 후 — id가 새 id라 리포트가 대상 트리를 가리킨다). */
  widgets: ComposerWidget[];
  /** 새로 이식된 body 노드(재사용분은 대상에 원래 있던 것이라 스캔 제외). */
  nodes: ComposerNode[];
  /** **새로 추가된** dataDefs/dataSources(재사용분 제외 — 기존 참조가 깨진 건 이 붙여넣기의 결과가 아니다). */
  dataDefs: DataDef[];
  dataSources: DataSourceDef[];
}

/**
 * 이번 붙여넣기가 대상 환경에서 **해소하지 못한 자산 참조**를 검출한다(클라이언트에서 판정 가능한 것만).
 *
 * 스캔 축(결정 2026-07-22 §이식성의 경계 표와 1:1):
 *   - `widgetKey`(InputWidget) → 등록형 위젯 레지스트리
 *   - `presetRef.presetId`(모든 위젯 base) → 대상 환경 프리셋 목록
 *   - 카탈로그 키 → 대상 카탈로그 인덱스
 *       · 위젯: `prefillFrom.catalogKey`(input/heading/formTable 텍스트 셀), `optionsFrom.catalogKey`,
 *         `submitAction.catalogKey`(modalTrigger)
 *       · 노드: 바인딩의 `catalogKey`(walkBindings — 필터 없이 전 바인딩. 카탈로그 필요 여부는 파생
 *         위젯 대상 여부와 무관하다)
 *       · 데이터: `DataSourceDef.primary`(manual은 '' 관례라 제외) / `lookups[].source` / `DataDef.prefillFrom.catalogKey`
 *
 * 위젯 순회는 **전 서브트리**(modalTrigger/repeat children 포함) — 값 스코프와 달리 카탈로그·레지스트리
 * 참조는 스코프 무관 전역이다(collectWidgetOverKeys와 같은 판단). 중복은 (축, 값)으로 1건만 보고한다.
 */
export function detectUnresolvedEnvironmentAssets(
  scan: WidgetPasteAssetScanInput,
  env: WidgetPasteEnvironment,
): WidgetPasteUnresolvedAsset[] {
  const registeredKeys =
    env.registeredWidgetKeys ?? new Set(REGISTERED_FORM_WIDGETS_META.map((m) => m.key));
  const assets: WidgetPasteUnresolvedAsset[] = [];
  const seen = new Set<string>();

  const push = (axis: WidgetPasteAssetAxis, value: string | undefined, widgetId?: string) => {
    if (!value) return;
    const dedupeKey = `${axis}|${value}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    assets.push(widgetId !== undefined ? { axis, value, widgetId } : { axis, value });
  };
  const checkCatalog = (key: string | undefined, widgetId?: string) => {
    if (!key) return;
    if (env.catalogIndex[key]) return;
    // 서버가 resolve 시점에 합성하는 행 키는 카탈로그 entry가 **없는 것이 정상**이라 미해소가 아니다
    // (shared 상수가 정본 — 서버 criteriaRowKeys와 같은 목록). 이 제외가 없으면 위험성평가 기준표
    // 참조가 붙여넣을 때마다 오탐으로 뜬다.
    if (SYNTHESIZED_CATALOG_KEYS.has(key)) return;
    push('catalogKey', key, widgetId);
  };

  const walkWidgets = (list: ComposerWidget[]) => {
    for (const w of list) {
      if (w.presetRef && !env.presetIds.has(w.presetRef.presetId)) {
        push('presetRef', w.presetRef.presetId, w.id);
      }
      if (w.kind === 'input') {
        if (w.widgetKey && !registeredKeys.has(w.widgetKey)) push('widgetKey', w.widgetKey, w.id);
        checkCatalog(w.prefillFrom?.catalogKey, w.id);
        checkCatalog(w.optionsFrom?.catalogKey, w.id);
      } else if (w.kind === 'group') {
        walkWidgets(w.children);
      } else if (w.kind === 'repeat') {
        walkWidgets(w.children);
      } else if (w.kind === 'modalTrigger') {
        checkCatalog(w.submitAction?.catalogKey, w.id);
        walkWidgets(w.children);
      } else if (w.kind === 'branch') {
        for (const slot of w.branches) walkWidgets(slot.children);
      } else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) {
            checkCatalog(cell.prefillFrom?.catalogKey, w.id);
            if (cell.widget) walkWidgets([cell.widget]);
          }
        }
      } else if (w.kind === 'separator' || w.kind === 'heading' || w.kind === 'spacer') {
        // DesignWidget — heading 자동 채움만 카탈로그를 본다.
        checkCatalog(w.prefillFrom?.catalogKey, w.id);
      } else {
        // exhaustiveness 가드 — 새 kind가 추가되면 **여기서 tsc가 깨진다**. 폴백 else로 받으면
        // 그 kind의 서브트리(children/슬롯)가 한 건도 스캔되지 않아 미해소를 **과소보고**한다
        // (이 모듈은 과다보고 쪽으로 안전한 방향이 정본). 런타임은 무해(no-op) — 미검출보다
        // 나쁠 게 없고, 붙여넣기 자체를 throw로 막을 사안은 아니다.
        // ⚠️ 값(`w`)이 아니라 **판별자(`w.kind`)** 를 never로 받는다 — DesignWidget은 한 인터페이스가
        // kind union('separator'|'heading'|'spacer')을 갖는 형태라, `||` 체인 else에서 TS가 그 타입
        // 자체는 제거하지 못하고 판별자만 never로 좁힌다(`w`로 받으면 TS2322).
        const _exhaustiveKind: never = w.kind;
        void _exhaustiveKind;
      }
    }
  };
  walkWidgets(scan.widgets);

  walkBindings(scan.nodes, (binding) => checkCatalog(binding.catalogKey));

  for (const def of scan.dataDefs) checkCatalog(def.prefillFrom?.catalogKey);
  for (const source of scan.dataSources) {
    checkCatalog(source.primary); // manual은 '' 관례 → push의 falsy 가드가 흡수
    for (const lookup of source.lookups ?? []) checkCatalog(lookup.source);
  }

  return assets;
}

/**
 * 결재 메타가 payload에 있었으나 **적용되지 않은** 경우를 미해소로 보고한다.
 *
 * 두 원인 모두 조용한 드롭이라 알려야 한다:
 *   ① coupled 정책 — 대상에 결재 설정이 하나라도 있으면 부분 혼합 방지로 미적용(applyPasteApprovalMeta).
 *   ② 배치 remap 실패 — approvalPlacement.nodeId가 이식 노드에 매핑되지 않아 dangling 방지로 드롭
 *      (호출처가 계산한 결과를 `placementDropped`로 받는다 — 그 remap 규칙은 호출처 소유).
 */
export function detectUnappliedApprovalMeta(
  payload: NodeClipboardPayload,
  targetBefore: Pick<ReportTemplate, 'approvalLineMeta' | 'approvalPlacement'>,
  placementDropped = false,
): WidgetPasteUnresolvedAsset[] {
  const assets: WidgetPasteUnresolvedAsset[] = [];
  const targetHasApproval =
    targetBefore.approvalLineMeta !== undefined || targetBefore.approvalPlacement !== undefined;
  if (targetHasApproval) {
    if (payload.approvalLineMeta) assets.push({ axis: 'approvalMeta', value: 'approvalLineMeta' });
    if (payload.approvalPlacement) assets.push({ axis: 'approvalMeta', value: 'approvalPlacement' });
    return assets;
  }
  if (placementDropped && payload.approvalPlacement) {
    assets.push({ axis: 'approvalMeta', value: 'approvalPlacement' });
  }
  return assets;
}

/** 이식 리포트 — 붙여넣기 결과의 3분류(이식됨 / 기존 재사용 / 미해소). */
export interface WidgetPasteReport {
  /** payload 출처가 대상과 같은 템플릿인가(표시 조건 근거 — isSameTemplatePaste). */
  sameTemplate: boolean;
  /** 이식됨 — 이번 붙여넣기로 대상에 **새로 생긴** 것. */
  ported: {
    /** 삽입된 위젯 수(붙여넣기 그 자체 — 항상 1 이상). */
    widgetCount: number;
    /** 새로 삽입된 body 노드 수(바인딩이 없는 정적 노드도 포함되므로 nodeKeys와 별개 축). */
    nodeCount: number;
    /** 새 노드가 소유하게 된 바인딩 키. */
    nodeKeys: string[];
    /** 새로 추가된 dataDefs/dataSources 키. */
    dataKeys: string[];
  };
  /** 기존 재사용 — payload에 있었으나 대상에 이미 있어 **추가하지 않은** 것(기존 우선·덮어쓰기 금지). */
  reused: {
    nodeKeys: string[];
    dataKeys: string[];
  };
  /** 미해소 — 자동으로 연결하지 못한 것(저작자 후속 조치 필요). */
  unresolved: {
    refs: PresetUnresolvedRef[];
    assets: WidgetPasteUnresolvedAsset[];
  };
}

/** buildWidgetPasteReport 입력 — 붙여넣기 계산 결과 + 대상 컨텍스트. */
export interface WidgetPasteReportInput {
  /** 실제로 붙여넣은 payload(모달 중첩 가드로 위젯이 걸러졌으면 그 걸러진 payload). */
  payload: NodeClipboardPayload;
  result: WidgetClipboardPasteResult;
  targetTemplateKey: string;
  env: WidgetPasteEnvironment;
  /** 대상 템플릿의 붙여넣기 **직전** 결재 설정(coupled 정책 판정 — 적용 후 상태로 보면 항상 "있음"이 된다). */
  targetApprovalBefore: Pick<ReportTemplate, 'approvalLineMeta' | 'approvalPlacement'>;
  /** approvalPlacement의 nodeId remap이 실패해 드롭됐는가(호출처 계산분). */
  approvalPlacementDropped?: boolean;
  /**
   * 대상 템플릿의 붙여넣기 **직전** body — 미해소 참조 재검증의 "대상에 이미 있는 키" 근거.
   *
   * **필수다**(옵셔널 금지) — 빠뜨리면 해소 근거가 줄어 2026-07-22에 고친 미해소 오탐이 조용히
   * 부활한다. 근거가 정말 없는 호출처는 `[]`를 **명시 전달**해 "없음"을 선언하게 한다.
   */
  targetBodyBefore: ComposerNode[];
  /** 대상 작성 트리의 붙여넣기 **직전** 상태 — 기존 위젯 bindingKey/위젯 id 근거(위와 동일 정책·필수). */
  targetWidgetTreeBefore: ComposerWidget[];
}

/**
 * 위젯 트리에서 **본문 스코프 값 키**를 모은다(미해소 재검증의 해소 근거 집합).
 *
 * collectWidgetBindingKeys(복사 시 body 역인덱스 조회용)와 목적이 달라 재사용하지 않는다 — 저쪽은
 * "body 노드에서 파생된 키"만 필요해 파생 input으로 좁히지만, 여기서 필요한 것은 "이 트리가 값을
 * 들고 있는 키 전체"다. treeOwned/등록형 위젯의 키도 실제 writeValues 키라 visibleWhen 등이 참조할 수
 * 있으므로 포함해야 하고, 빠뜨리면 그만큼 오탐이 남는다.
 *
 * 스코프 경계는 rc의 bindingKey 축 재매핑과 동일 — group/branch는 투명 컨테이너라 하강하고,
 * modalTrigger children(모달 스코프)·repeat children(행 상대키)은 본문 스코프가 아니라 하강하지 않는다.
 * rc가 미해소로 **보고하는** 축도 본문 스코프뿐이므로 대조 집합이 정확히 대응한다.
 * repeat.over(데이터셋 키)·modalTrigger 자신의 키(모달 UI 상태)는 본문 스코프 값이라 포함한다.
 *
 * ⚠️ rc `collectPlacedInputKeys`와 **같은 집합이 아니다** — 겹치는 것은 `repeat.over`를 포함한다는
 *    점뿐이고, 저쪽은 modalTrigger의 자기 키·children을 **의도적으로 스킵**한다. 여기서 그 키까지
 *    모으는 것은 이 함수만의 확장이다(해소 근거는 넓을수록 오탐이 줄고, 좁으면 오탐이 남는다).
 *    두 함수를 "같은 판단"으로 보고 통합하면 미해소 오탐이 되살아난다.
 */
function collectBodyScopeWidgetKeys(widgets: ComposerWidget[]): Set<string> {
  const keys = new Set<string>();
  const add = (key: string | undefined) => {
    if (key) keys.add(key);
  };
  const walk = (list: ComposerWidget[]) => {
    for (const w of list) {
      if (w.kind === 'input') {
        add(w.bindingKey);
      } else if (w.kind === 'group') {
        walk(w.children);
      } else if (w.kind === 'branch') {
        for (const slot of w.branches) walk(slot.children);
      } else if (w.kind === 'repeat') {
        add(w.over);
      } else if (w.kind === 'modalTrigger') {
        add(w.bindingKey);
      } else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) {
            if (cell.widget) add(cell.widget.bindingKey);
          }
        }
      }
    }
  };
  walk(widgets);
  return keys;
}

/**
 * 위젯 트리의 **모든 위젯 id**를 모은다(위젯 id 축 재검증 근거).
 * id는 트리 전역 유일이라 스코프 경계 없이 전 서브트리를 훑는다(rc actionItem.opensWidgetId 재매핑과 동형).
 */
function collectAllWidgetIds(widgets: ComposerWidget[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: ComposerWidget[]) => {
    for (const w of list) {
      ids.add(w.id);
      if (w.kind === 'group' || w.kind === 'repeat' || w.kind === 'modalTrigger') {
        walk(w.children);
      } else if (w.kind === 'branch') {
        for (const slot of w.branches) walk(slot.children);
      } else if (w.kind === 'formTable') {
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) {
            if (cell.widget) walk([cell.widget]);
          }
        }
      }
    }
  };
  walk(widgets);
  return ids;
}

/**
 * rc가 보고한 미해소 참조를 **실제 붙여넣기 결과와 대조해 거른다**(오탐 제거 — 2026-07-22 사용자 제보).
 *
 * rc `clonePresetSubtreeForInsert`는 **위젯 클론 단계**에서 미해소를 낸다. 그 단계는 자기가 아는 위젯 키만
 * 보므로, 이 붙여넣기가 **body 노드까지 함께 이식**해 그 노드가 참조 키를 소유하게 되는 사실을 알 수 없다
 * (실제로 같은 모달의 "새 바인딩" 목록에 그 키가 그대로 있었다). 그래서 붙여넣기 **결과**를 근거로 재검증한다.
 *
 * 축 구분(rc PresetRefAxis 7종):
 *   - `actionItem.opensWidgetId` = **위젯 id 축** → 붙여넣은 위젯의 새 id + 대상 트리 기존 위젯 id와 대조.
 *   - 나머지 6종 = **bindingKey 축** → 이식/재사용 노드 키 + 붙여넣은 위젯 키 + 대상 기존 키와 대조.
 *   - 단 `writes.target`의 **배열 일괄 형식**(`defKey[].field.path`)만 값이 bindingKey가 아니라 데이터셋
 *     참조다 → `[].` 앞 defKey를 **병합 후 최종 dataDefs/dataSources 키**와 대조한다(=붙여넣기 후 대상에
 *     그 데이터셋이 존재하는가). 스칼라 키로 오인해 대조하면 언제나 미매칭이라 영구 오탐이 된다.
 *
 * 방향성: 근거가 부족하면(대상 스냅샷 미제공 등) **보고를 유지**한다 — 과소보고(조용한 유실)보다 과다보고가 안전.
 */
function filterResolvedRefs(
  refs: PresetUnresolvedRef[],
  resolved: {
    bindingKeys: ReadonlySet<string>;
    widgetIds: ReadonlySet<string>;
    dataKeys: ReadonlySet<string>;
  },
): PresetUnresolvedRef[] {
  return refs.filter((ref) => {
    if (ref.axis === 'actionItem.opensWidgetId') return !resolved.widgetIds.has(ref.value);
    // 일괄 형식 판별·해체는 rc 정본(parseBatchWriteTarget) — 여기서 sentinel을 다시 파싱하면
    // 문법 변경 시 조용히 미매칭으로 떨어져 모든 일괄 writes가 영구 오탐이 된다.
    const batch = ref.axis === 'writes.target' ? parseBatchWriteTarget(ref.value) : null;
    if (batch) {
      return !resolved.dataKeys.has(batch.defKey);
    }
    return !resolved.bindingKeys.has(ref.value);
  });
}

/**
 * 붙여넣기 결과를 3분류 리포트로 환산한다(순수 — 표시 여부 판단은 shouldShowWidgetPasteReport).
 *
 * 재사용 데이터 키는 "payload에 실려 있었으나 addedKeys에 없는 것" = 대상에 같은 key가 이미 있어
 * mergeClipboardDependencies가 내용 비교 없이 skip한 것들이다(그 규칙의 정확한 여집합).
 */
export function buildWidgetPasteReport(input: WidgetPasteReportInput): WidgetPasteReport {
  const { payload, result, env } = input;
  const addedData = new Set([...result.addedDefKeys, ...result.addedSourceKeys]);
  const reusedDataKeys: string[] = [];
  const payloadDataKeys = [
    ...payload.dataDefs.map((d) => d.key),
    ...payload.dataSources.map((s) => s.key),
  ];
  for (const key of payloadDataKeys) {
    if (addedData.has(key)) continue;
    if (reusedDataKeys.includes(key)) continue;
    reusedDataKeys.push(key);
  }

  const assets = [
    ...detectUnresolvedEnvironmentAssets(
      {
        widgets: result.widgets,
        nodes: result.nodes,
        dataDefs: result.dataDefs.filter((d) => result.addedDefKeys.includes(d.key)),
        dataSources: result.dataSources.filter((s) => result.addedSourceKeys.includes(s.key)),
      },
      env,
    ),
    ...detectUnappliedApprovalMeta(
      payload,
      input.targetApprovalBefore,
      input.approvalPlacementDropped ?? false,
    ),
  ];

  // 미해소 참조 재검증 — rc 위젯 클론 단계가 모르는 "함께 이식된 body 노드/데이터"까지 근거에 넣는다.
  const resolvedBindingKeys = new Set<string>([
    ...result.portedNodeKeys,
    ...result.reusedNodeKeys,
    ...collectBodyScopeWidgetKeys(result.widgets),
    ...buildBodyBindingIndex(input.targetBodyBefore).keys(),
    ...collectBodyScopeWidgetKeys(input.targetWidgetTreeBefore),
  ]);
  const resolvedWidgetIds = new Set<string>([
    ...collectAllWidgetIds(result.widgets),
    ...collectAllWidgetIds(input.targetWidgetTreeBefore),
  ]);
  // 데이터 키는 **병합 후 전체**(result.dataDefs/dataSources) — 배열 일괄 writes가 가리키는 데이터셋이
  // 붙여넣기 후 대상에 존재하는가가 판정 기준이다(추가분만 보면 재사용된 데이터셋이 오탐이 된다).
  const resolvedDataKeys = new Set<string>([
    ...result.dataDefs.map((d) => d.key),
    ...result.dataSources.map((s) => s.key),
  ]);
  const refs = filterResolvedRefs(result.unresolvedRefs, {
    bindingKeys: resolvedBindingKeys,
    widgetIds: resolvedWidgetIds,
    dataKeys: resolvedDataKeys,
  });

  return {
    sameTemplate: isSameTemplatePaste(payload, input.targetTemplateKey),
    ported: {
      widgetCount: result.widgets.length,
      nodeCount: result.nodes.length,
      nodeKeys: [...result.portedNodeKeys],
      dataKeys: [...result.addedDefKeys, ...result.addedSourceKeys],
    },
    reused: {
      nodeKeys: [...result.reusedNodeKeys],
      dataKeys: reusedDataKeys,
    },
    unresolved: {
      refs,
      assets,
    },
  };
}

/**
 * 리포트 모달을 띄울지 판정한다 — **마찰 최소화가 규칙의 목적**이다(항상 띄우지 않는다).
 *
 * 기존 노드 붙여넣기 UX 선례(같은 템플릿=모달 없이 즉시 / 다른 템플릿=선택 모달)와 같은 결:
 *   ① 미해소가 하나라도 있으면 표시 — 조용한 유실 방지가 이 모달의 존재 이유다.
 *   ② 다른 템플릿 붙여넣기로 **의존물이 함께 이식됐으면** 표시 — 대상 템플릿에 노드·데이터가 새로
 *      생기는 것은 사용자가 지시하지 않은 부수 변경이라 알려야 한다.
 *   ③ 그 외(같은 템플릿 + 미해소 0)는 표시하지 않는다 — 기존처럼 조용히 붙는다.
 *
 * ②에서 위젯 수는 보지 않는다. 위젯 삽입은 사용자가 방금 요청한 동작 그 자체라 보고할 "부수 결과"가
 * 아니다(그것까지 세면 다른 템플릿 붙여넣기가 매번 모달이 된다).
 */
export function shouldShowWidgetPasteReport(report: WidgetPasteReport): boolean {
  if (report.unresolved.refs.length > 0 || report.unresolved.assets.length > 0) return true;
  if (report.sameTemplate) return false;
  return report.ported.nodeCount > 0 || report.ported.dataKeys.length > 0;
}
